'use client';

import { useTranslations, useLocale } from 'next-intl';
import Link from 'next/link';
import Image from 'next/image';
import { getLocalizedRoute, Locale } from '@/config/routes';
import { NavhausSignature } from './NavhausSignature';
import { MapPin, Phone, Mail, Facebook, Instagram } from 'lucide-react';

const Footer = () => {
  const t = useTranslations('nav');
  const footer = useTranslations('footer');
  const locale = useLocale() as Locale;

  const navLinks = [
    { href: getLocalizedRoute('home', locale), label: t('home') },
    { href: getLocalizedRoute('about', locale), label: t('about') },
    { href: getLocalizedRoute('services', locale), label: t('services') },
    { href: getLocalizedRoute('blog', locale), label: t('blog') },
    { href: getLocalizedRoute('contact', locale), label: t('contact') },
  ];

  return (
    <footer className="mt-10 px-4 pb-4">
      <div className="max-w-7xl mx-auto glass rounded-3xl p-10 shadow-lg border border-white/10">
        <div className="grid md:grid-cols-3 gap-12 mb-10">
          {/* Logo and Description */}
          <div>
            <Link href={getLocalizedRoute('home', locale)} className="mb-4 block">
              <Image
                src="/images/logo.svg"
                alt="BubbleZone"
                width={180}
                height={41}
                className="h-10 w-auto"
              />
            </Link>
            <p className="text-foreground/70 max-w-md mb-6">
              {footer('description')}
            </p>
            {/* Social Links */}
            <div className="flex gap-3">
              <a
                href="https://www.facebook.com/profile.php?id=61585986996992"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-xl gradient-purple flex items-center justify-center hover:scale-110 transition-transform"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5 text-white" strokeWidth={1.5} />
              </a>
              <a
                href="https://www.instagram.com/bubblezone.laundry"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-xl gradient-pink flex items-center justify-center hover:scale-110 transition-transform"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5 text-white" strokeWidth={1.5} />
              </a>
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-semibold text-lg mb-4">{t('contact')}</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3 text-foreground/70 hover:text-primary transition-colors group">
                <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5 text-foreground" strokeWidth={1.5} />
                <div>
                  <p>Sestara Elčić 44</p>
                  <p>Sarajevo, BiH</p>
                </div>
              </div>
              <a href="tel:+38762752758" className="flex items-start gap-3 text-foreground/70 hover:text-primary transition-colors group">
                <Phone className="w-5 h-5 flex-shrink-0 mt-0.5 text-foreground" strokeWidth={1.5} />
                <div>
                  <span className="block">+387 62 752 758</span>
                  <span className="text-sm opacity-70">Viber & WhatsApp</span>
                </div>
              </a>
              <a href="mailto:info@bzs.ba" className="flex items-center gap-3 text-foreground/70 hover:text-primary transition-colors group">
                <Mail className="w-5 h-5 flex-shrink-0 text-foreground" strokeWidth={1.5} />
                <span>info@bzs.ba</span>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-lg mb-4">{footer('quickLinks')}</h3>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-foreground/70 hover:text-primary transition-colors inline-flex items-center gap-2 group"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-primary opacity-30 group-hover:opacity-100 transition-opacity"></span>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-foreground/10 pt-10 flex flex-col md:flex-row justify-between md:items-center gap-4">
          <p className="text-foreground/60 text-sm">
            {footer('copyright')} {new Date().getFullYear()}
          </p>
          <NavhausSignature />
        </div>
      </div>
    </footer>
  );
};

export default Footer;
